import { apiFetch } from '@/shared/api/client'
import { isMockApi } from '@/shared/env'

export type ApiPeerKitchen = {
  id: string
  code: string
  name: string
}

export type ApiKitchenTransferLine = {
  id?: string
  item_type?: string
  product_name?: string
  material_name?: string
  production_date?: string
  uom?: string
  qty: number
  qty_good?: number
  qty_damaged?: number
  qty_shortage?: number
}

export type ApiKitchenTransfer = {
  id: string
  code: string
  target_kitchen_name?: string
  source_kitchen_name?: string
  delman_name: string
  status: string
  receivable?: boolean
  updated_at: string
  dispatched_at?: string
  lines: ApiKitchenTransferLine[]
}

export type TodayKitchenTransfersResponse = {
  outgoing: ApiKitchenTransfer[]
  incoming: ApiKitchenTransfer[]
}

export type KitchenTransferLineInput = {
  itemType: 'product' | 'material'
  productName?: string
  materialName?: string
  productionDate?: string
  uom?: string
  qty: number
}

export type KitchenTransferSavePayload = {
  targetKitchenName: string
  delmanName: string
  lines: KitchenTransferLineInput[]
  pin?: string
}

export type KitchenTransferReceiveLine = {
  itemType: 'product' | 'material'
  productName?: string
  materialName?: string
  productionDate?: string
  uom?: string
  qtyGood: number
}

const MOCK_PEER_KITCHENS: ApiPeerKitchen[] = [
  { id: 'k-b', code: 'DAPUR-B', name: 'Dapur B Kemang' },
]

const MOCK_OUTGOING: ApiKitchenTransfer[] = []

export async function fetchPeerKitchens(_deviceId: string): Promise<ApiPeerKitchen[]> {
  if (isMockApi) return MOCK_PEER_KITCHENS
  const res = await apiFetch<{ kitchens: ApiPeerKitchen[] }>('/kitchen/peer-kitchens')
  return res.kitchens ?? []
}

export async function fetchTodayKitchenTransfers(_deviceId: string): Promise<TodayKitchenTransfersResponse> {
  if (isMockApi) {
    return { outgoing: MOCK_OUTGOING, incoming: [] }
  }
  return apiFetch<TodayKitchenTransfersResponse>('/kitchen/kitchen-transfers/today')
}

export async function saveKitchenTransfer(
  transferRef: string,
  payload: KitchenTransferSavePayload,
): Promise<{ transfer_id: string; transfer_code: string; updated_at: string }> {
  if (isMockApi) {
    await new Promise((r) => setTimeout(r, 300))
    return {
      transfer_id: transferRef,
      transfer_code: transferRef,
      updated_at: new Date().toISOString(),
    }
  }
  return apiFetch(`/kitchen/kitchen-transfers/${encodeURIComponent(transferRef)}/save`, {
    method: 'POST',
    body: JSON.stringify({
      target_kitchen_name: payload.targetKitchenName,
      delman_name: payload.delmanName,
      lines: payload.lines.map((l) => ({
        item_type: l.itemType,
        product_name: l.productName ?? '',
        material_name: l.materialName ?? '',
        production_date: l.productionDate ?? '',
        uom: l.uom ?? '',
        qty: l.qty,
      })),
      pin: payload.pin ?? '',
    }),
  })
}

export async function dispatchKitchenTransfer(
  transferRef: string,
  payload: KitchenTransferSavePayload & { pin: string },
): Promise<{ updated_at: string }> {
  if (isMockApi) {
    await new Promise((r) => setTimeout(r, 400))
    return { updated_at: new Date().toISOString() }
  }
  return apiFetch(`/kitchen/kitchen-transfers/${encodeURIComponent(transferRef)}/dispatch`, {
    method: 'POST',
    body: JSON.stringify({
      target_kitchen_name: payload.targetKitchenName,
      delman_name: payload.delmanName,
      lines: payload.lines.map((l) => ({
        item_type: l.itemType,
        product_name: l.productName ?? '',
        material_name: l.materialName ?? '',
        production_date: l.productionDate ?? '',
        uom: l.uom ?? '',
        qty: l.qty,
      })),
      pin: payload.pin,
    }),
  })
}

export async function scanKitchenTransferQr(token: string): Promise<{
  transfer_id: string
  transfer_code: string
  source_kitchen_name: string
  delman_name: string
}> {
  if (isMockApi) {
    await new Promise((r) => setTimeout(r, 300))
    return {
      transfer_id: 'mock-transfer',
      transfer_code: 'KTR-MOCK',
      source_kitchen_name: 'Dapur A',
      delman_name: 'Pak Budi',
    }
  }
  return apiFetch('/kitchen/kitchen-transfers/scan', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

export async function receiveKitchenTransfer(
  transferRef: string,
  pin: string,
  lines: KitchenTransferReceiveLine[],
): Promise<{ updated_at: string }> {
  if (isMockApi) {
    await new Promise((r) => setTimeout(r, 400))
    return { updated_at: new Date().toISOString() }
  }
  return apiFetch(`/kitchen/kitchen-transfers/${encodeURIComponent(transferRef)}/receive`, {
    method: 'POST',
    body: JSON.stringify({
      pin,
      lines: lines.map((l) => ({
        item_type: l.itemType,
        product_name: l.productName ?? '',
        material_name: l.materialName ?? '',
        production_date: l.productionDate ?? '',
        uom: l.uom ?? '',
        qty_good: l.qtyGood,
        qty_damaged: 0,
        qty_shortage: 0,
      })),
    }),
  })
}

export function newTransferCode(): string {
  const d = new Date()
  const tag = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
  return `KTR-${tag}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
}

export type ApiMaterialStockItem = {
  materialName: string
  uom: string
  stockAvailable: number
}

export async function fetchKitchenMaterialStock(_deviceId: string): Promise<ApiMaterialStockItem[]> {
  if (isMockApi) {
    return [
      { materialName: 'Tepung Cakra Kembar', uom: 'kg', stockAvailable: 25 },
      { materialName: 'Gula pasir', uom: 'kg', stockAvailable: 15 },
      { materialName: 'Mentega Wysman', uom: 'kg', stockAvailable: 8 },
      { materialName: 'Susu UHT', uom: 'l', stockAvailable: 20 },
      { materialName: 'Ragi instan', uom: 'kg', stockAvailable: 3 },
      { materialName: 'Garam halus', uom: 'kg', stockAvailable: 5 },
      { materialName: 'Telur ayam', uom: 'kg', stockAvailable: 10 },
    ]
  }
  const res = await apiFetch<{ stock: ApiMaterialStockItem[] }>('/kitchen/material-stock')
  return res.stock ?? []
}
